import React, { useEffect } from "react";
import { Typography, Stack, Chip, styled, Card, IconButton, Box } from "@mui/material";

import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { userService } from "../../../Core/Api/services";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import EditUserRolePanel from "../EditUserRolePanel/edit-user-role-panel";
import UserGroupChip from "../../Misc/Chips/UserGroupChip/user-group-chip";
import { useTranslation } from "react-i18next";
import { produce } from "immer";

export interface ManageRolesPanelProps {
  width?: number;
  open: boolean;
  onClose?: React.MouseEventHandler;
  onCloseUserRole?: () => void;
}

const ManageRolesPanel: React.FC<ManageRolesPanelProps> = (props) => {
  const { open, onClose, onCloseUserRole } = props;
  const { t } = useTranslation();
  const { active: editRolePanel, toggle: toggleEditRolePanel } = useToggle(false);
  const [selectedRole, setSelectedRole] = React.useState<DbUserGroupRoleInfos | null>(null);
  const [roles, setRoles] = React.useState<DbUserGroupRoleInfos[]>([]);

  useEffect(() => {
    if (editRolePanel) toggleEditRolePanel();
  }, [open]);

  useEffect(() => {
    userService.GetGroupRolesList().then(function (response) {
      if (response.success) {
        setRoles(response.groupRoles || []);
      }
    });
  }, []);

  const onClickDelete = (id: number) => {
    userService.DeleteGroupRole(id).then(function (response) {
      if (response.success) {
        setRoles(produce((draft) => (draft = draft.filter((x) => x.id !== id))));
      }
    });
  };

  const onConfirm = (role: DbUserGroupRoleInfos, isCreation: boolean) => {
    if (isCreation) {
      setRoles(
        produce((draft) => {
          draft.push(role);
        })
      );
    } else {
      setRoles(
        produce((draft) => {
          let index = draft.findIndex((x) => x.id === role.id);
          if (index !== -1) draft[index] = role;
        })
      );
    }

    toggleEditRolePanel();
  };

  const toggleRolePanel = (role: DbUserGroupRoleInfos | null) => {
    setSelectedRole(role);
    toggleEditRolePanel();
  };

  return (
    <>
      {!editRolePanel ? (
        <Panel
          header={<PanelHeader title={t("common:roles")} onClose={onClose as PanelHeaderProps["onClose"]} />}
          open={open}
          onClose={onClose}
        >
          <Stack spacing={1}>
            {roles.map((role) => (
              <CardRoles key={role.id}>
                <Stack alignItems="flex-start">
                  <UserGroupChip roleId={role.id} roles={roles} />
                  <Typography variant="body2" color="text.tier" mt={1}>
                    {role.description}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" ml={1.5}>
                  <IconButton onClick={() => toggleRolePanel(role)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" onClick={() => onClickDelete(role.id)} disabled={role.isSystem === true}>
                    <DeleteOutlinedIcon color="error" fontSize="small" />
                  </IconButton>
                </Stack>
              </CardRoles>
            ))}
            <Box>
              <EmptyCardRole sx={{ mt: 1 }} onClick={() => toggleRolePanel(null)}>
                <Stack alignItems="flex-start">
                  <Chip label={t("chip:label.addCustomRole")} />
                  <Typography variant="body2" color="text.tier" mt={1}>
                    {t("panel:manageRolesPanel.body.createDefaultPermissions")}
                  </Typography>
                </Stack>
              </EmptyCardRole>
            </Box>
          </Stack>
        </Panel>
      ) : (
        <EditUserRolePanel
          open={editRolePanel}
          role={selectedRole}
          allRoles={roles}
          onClose={() => {
            toggleEditRolePanel();
          }}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
};

const CardRoles = styled(Card)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  border: `1px solid transparent`,
  transition: "border-color .3s, background .3s",

  "&:hover": {
    borderColor: theme.palette.backgroundAlpha.border,
    backgroundColor: theme.palette.backgroundAlpha.hover,
  },
}));

const EmptyCardRole = styled(Card)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  border: `1px dashed ${theme.palette.backgroundAlpha.border}`,
  background: "transparent",
  transition: "border-color .3s, background .3s",
  cursor: "pointer",

  "&:hover": {
    borderColor: theme.palette.backgroundAlpha.border,
    backgroundColor: theme.palette.backgroundAlpha.hover,
  },
}));

export default ManageRolesPanel;
